import * as _ from 'lodash';
import moment from 'moment';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import { FormGroup, InputGroup, Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { get } from '../../../../../redux/api';
import Button from '../../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../../generic/Button/button-constants';
import ButtonGroup from '../../../../generic/Button/ButtonGroup';
import IconButton from '../../../../generic/Button/IconButton';
import InlineLoaderButton from '../../../../generic/Button/InlineLoaderButton';
import { ErrorAlert } from '../../../../generic/ErrorAlert';
import Flex from '../../../../generic/Flex';
import FormLabel from '../../../../generic/Forms/FormLabel';
import LabeledSelect from '../../../../generic/Forms/LabeledSelect';
import LabeledTextInput from '../../../../generic/Forms/LabeledTextInput';
import { icons } from '../../../../generic/Icon/icons';
import AsyncSelect from '../../../../generic/Select/AsyncSelect';
import SimpleReactTable from '../../../../generic/SimpleReactTable';
import TableHeader from '../../../../generic/TableHeader';
import { expirationOptions } from '../constants';
import type { ReleaseGroupsResponse } from '../../../../../../shared/projectsV2/response';
import type { ExpirationOption, ExpirationValue, ReleaseGroupOption } from '../types';

import css from '../ModalCreatePortalToken.module.less';

const ReleaseGroupCell = ({ original: { label } }: { original: { label: string } }) => {
  return <span> {label}</span>;
};

const createActionsCell = (onRemoveFromTable: (groupId: number) => void) =>
  function ({ original: { value } }: { original: { value: number } }) {
    return <IconButton icon={icons.xMark} id="delete" onClick={() => onRemoveFromTable(value)} />;
  };

type Props = {
  shouldShowModal: boolean;
  errors: string[];
  name: string;
  setName: (name: string) => void;
  expiration: Date | null;
  expirationOption?: ExpirationOption;
  setExpirationOption: (expirationOption: ExpirationOption) => void;
  selectedReleaseGroups: ReleaseGroupOption[];
  setSelectedReleaseGroups: (releaseGroups: ReleaseGroupOption[]) => void;
  onHide: () => void;
  onMutateToken: (token: {
    name: string;
    expiration: ExpirationValue | null;
    releaseGroups: number[];
  }) => Promise<void>;
  type: 'Create' | 'Edit';
  isSaving: boolean;
};

/**
 * Modal for creating or editing a portal token.
 */
const MutatePortalTokenModal: VoidFunctionComponent<Props> = ({
  shouldShowModal,
  errors,
  name,
  setName,
  expiration,
  expirationOption,
  setExpirationOption,
  selectedReleaseGroups,
  setSelectedReleaseGroups,
  onHide,
  onMutateToken,
  type = 'Create',
  isSaving,
}) => {
  const [inputReleaseGroups, setInputReleaseGroups] = useState<ReleaseGroupOption[]>([]);
  // Without the key forcing a re-render, the async select will not omit the selected release groups from the dropdown.
  // This creates a poor user experience.
  // This solution is inspired by: https://github.com/JedWatson/react-select/issues/1879#issuecomment-316871520
  const [asyncSelectKey, setAsyncSelectKey] = useState(0);
  useEffect(() => {
    setAsyncSelectKey((previousAsyncSelectKey) => previousAsyncSelectKey + 1);
  }, [selectedReleaseGroups]);
  const fetchReleaseGroupOptions = useCallback(
    async (titleFilter: string) => {
      const titleQuery = qs.stringify({ title: titleFilter });
      const { data } = await get<ReleaseGroupsResponse>(`/api/v2/release-groups?${titleQuery}`);

      return (
        (data?.releaseGroups || []).map(({ id, title }) => ({
          value: _.toNumber(id),
          label: title,
        })) || []
      ).filter(({ value: releaseGroupValue }) => {
        const isNotInInput = !selectedReleaseGroups.some(
          ({ value: selectedValue }) => releaseGroupValue === selectedValue,
        );
        const isNotInTable = !inputReleaseGroups.some(({ value: inputValue }) => releaseGroupValue === inputValue);

        return isNotInInput && isNotInTable;
      });
    },
    [inputReleaseGroups, selectedReleaseGroups],
  );

  const onHideHelper = () => {
    onHide();
    setInputReleaseGroups([]);
  };

  return (
    <Modal show={shouldShowModal} onHide={onHideHelper}>
      <div className={css.modalWrapper}>
        <Modal.Header className={css.header} closeButton>
          <h1 className={css.heading}>{type} token</h1>
          <div className={css.headingDescription}>
            {type} and share a token allowing a user to access one or more release groups in the portal.
          </div>
        </Modal.Header>
        <Modal.Body className={css.body}>
          {!_.isEmpty(errors) && <ErrorAlert status={{ errors }} />}
          <div className="margin-bottom-lg">
            <div>
              <FormGroup controlId="repoInputForm" style={{ display: 'flex', flexDirection: 'column' }}>
                <InputGroup className={`input-group input-group-md ${css.portalTokenFormInput}`}>
                  <LabeledTextInput
                    isRequired
                    size="small"
                    label="Name"
                    value={name}
                    onChange={setName}
                  />
                </InputGroup>
                <div className={css.portalTokenFormInput}>
                  <LabeledSelect
                    key={asyncSelectKey}
                    label="Expires"
                    value={expirationOption}
                    options={expirationOptions}
                    id="token-expiration"
                    onChange={(option) => {
                      if (option) {
                        setExpirationOption(option);
                      }
                    }}
                  />
                  {expiration && moment(expiration).isValid() && (
                    <div className={css.expirationDescription}>Expires on {expiration.toLocaleDateString()}</div>
                  )}
                </div>
                <FormLabel label="Release groups">
                  <div className={css.releaseGroupSelectDescription}>Grant or revoke access to release groups</div>
                  <Flex justify="flex-start">
                    <AsyncSelect
                      key={asyncSelectKey.toString()}
                      className={css.addReleaseGroupSelect}
                      isMulti
                      loadOptions={fetchReleaseGroupOptions}
                      defaultOptions
                      isClearable
                      placeholder="Search by release group name"
                      value={inputReleaseGroups}
                      onChange={setInputReleaseGroups}
                      id="add-release-groups-select"
                    />
                    <Button
                      size={BUTTON_SIZE.MEDIUM}
                      onClick={() => {
                        const dupedReleaseGroups = selectedReleaseGroups.concat(inputReleaseGroups);
                        const dedupedReleaseGroups = _.uniqBy(dupedReleaseGroups, ({ value }) => value);
                        setSelectedReleaseGroups(dedupedReleaseGroups);
                        setInputReleaseGroups([]);
                      }}
                    >
                      Add
                    </Button>
                  </Flex>
                </FormLabel>
                <SimpleReactTable
                  data={selectedReleaseGroups}
                  columns={[
                    {
                      id: 'releaseGroup',
                      sortable: false,
                      Header: <TableHeader title="Release Group" />,
                      Cell: ReleaseGroupCell,
                      resizable: false,
                    },
                    {
                      style: { justifyContent: 'center' },
                      id: 'actions',
                      sortable: false,
                      Header: <TableHeader title="Actions" center />,
                      Cell: createActionsCell((groupId: number) => {
                        const toKeep = selectedReleaseGroups.filter(({ value }) => value !== groupId);
                        setSelectedReleaseGroups(toKeep);
                      }),
                      accessor: 'id',
                      resizable: false,
                      width: 120,
                    },
                  ]}
                  defaultSortMode={[]}
                  onSortedChanged={() => {}}
                  minRows={3}
                  getTrGroupProps={() => ({ style: { border: 'none' } })}
                />
              </FormGroup>
            </div>
          </div>
        </Modal.Body>
      </div>
      <Modal.Footer bsClass={css.footer}>
        <ButtonGroup spacing="xs">
          <Button onClick={onHide} type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM}>
            Cancel
          </Button>
          &nbsp;&nbsp;
          <InlineLoaderButton
            size={BUTTON_SIZE.MEDIUM}
            onClick={() =>
              onMutateToken({
                name,
                expiration: expirationOption?.value || null,
                releaseGroups: selectedReleaseGroups.map(({ value }) => Number(value)),
              })}
            isLoading={isSaving}
            isDisabled={_.isEmpty(name.trim())}
          >
            {type === 'Create' ? 'Create' : 'Save'}
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default MutatePortalTokenModal;
